import { Button, Input, Select, formatCurrency } from '@lipaflow/ui'

/** A regular monthly charge on a unit (rules 8.1–8.4): a fixed amount, and where its money goes. */
export interface RegularCharge { category: string; amount: string; description?: string | null; fund?: 'owner' | 'building_fund' | null }

/** Rule 8.1: the four regular charges. */
export const CHARGE_TYPES = [
  { value: 'service_charge', label: 'Service charge' },
  { value: 'water', label: 'Water' },
  { value: 'garbage', label: 'Garbage' },
  { value: 'parking', label: 'Parking' },
]
const LABEL: Record<string, string> = Object.fromEntries(CHARGE_TYPES.map((t) => [t.value, t.label]))

/** Where a charge's money goes when nobody chose: service charge, and everything in a charges-only building, to the fund. */
export function defaultFund(category: string, chargesOnly: boolean): 'owner' | 'building_fund' {
  return chargesOnly || category === 'service_charge' ? 'building_fund' : 'owner'
}

export function fundOf(charge: RegularCharge, chargesOnly: boolean): 'owner' | 'building_fund' {
  return chargesOnly ? 'building_fund' : charge.fund || defaultFund(charge.category, chargesOnly)
}

const money = (value: string | number | null | undefined) => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function RegularChargesEditor({ value, onChange, chargesOnly }: {
  value: RegularCharge[]; onChange: (v: RegularCharge[]) => void; chargesOnly: boolean
}) {
  const update = (index: number, change: Partial<RegularCharge>) => onChange(value.map((row, i) => i === index ? { ...row, ...change } : row))
  const next = CHARGE_TYPES.find((t) => !value.some((v) => v.category === t.value))
  return <fieldset className="space-y-3">
    <legend className="text-body font-semibold text-ink">Regular monthly charges</legend>
    <p className="text-body-sm text-ink-muted">
      A fixed amount each month, billed with rent. The tenant pays them; while the unit has no tenant, the owner pays
      what goes to the building fund. A change applies to the next bills, never to bills already sent.
    </p>
    {value.map((row, i) => {
      const name = LABEL[row.category] ?? 'Charge'
      return <div key={i} className="grid gap-3 rounded-card border border-hairline p-3 sm:grid-cols-3">
        <Select label="Type" value={row.category}
          options={CHARGE_TYPES.filter((t) => t.value === row.category || !value.some((v) => v.category === t.value))}
          onChange={(e) => update(i, { category: e.target.value, fund: defaultFund(e.target.value, chargesOnly) })} />
        <Input label={`${name} per month (KES)`} type="number" required min="0.01" step="0.01" value={row.amount}
          onChange={(e) => update(i, { amount: e.target.value })} />
        {chargesOnly
          ? <p className="self-end pb-2 text-body-sm text-ink-muted">Goes to the building fund</p>
          : <Select label={`Where the ${name.toLowerCase()} goes`} value={fundOf(row, chargesOnly)}
              options={[{ value: 'building_fund', label: 'Building fund' }, { value: 'owner', label: "The unit's owner" }]}
              onChange={(e) => update(i, { fund: e.target.value as RegularCharge['fund'] })} />}
        <div className="sm:col-span-3 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(value.filter((_, index) => index !== i))}>
            Remove {name.toLowerCase()}
          </Button>
        </div>
      </div>
    })}
    {next && <Button type="button" variant="secondary" size="sm"
      onClick={() => onChange([...value, { category: next.value, amount: '', fund: defaultFund(next.value, chargesOnly) }])}>
      Add a monthly charge
    </Button>}
  </fieldset>
}

/**
 * The monthly bill before saving: what the tenant pays each month, and, while the unit has no tenant,
 * what its owner pays (the charges that go to the building fund; rule 8.2).
 */
export function BillPreview({ rent, charges, chargesOnly, hasOwner, ownersApply = true }: {
  rent: string; charges: RegularCharge[]; chargesOnly: boolean; hasOwner: boolean
  /** False for a landlord letting their own units: there is no owner to bill. */
  ownersApply?: boolean
}) {
  const rows = [
    ...(!chargesOnly && money(rent) > 0 ? [{ label: 'Rent', amount: money(rent), fund: 'owner' as const }] : []),
    ...charges.filter((c) => money(c.amount) > 0).map((c) => ({ label: LABEL[c.category] ?? 'Charge', amount: money(c.amount), fund: fundOf(c, chargesOnly) })),
  ]
  if (!rows.length) return null
  const tenantTotal = rows.reduce((sum, r) => sum + r.amount, 0)
  const vacant = rows.filter((r) => r.label !== 'Rent' && r.fund === 'building_fund')
  const ownerTotal = vacant.reduce((sum, r) => sum + r.amount, 0)
  return <section aria-label="Monthly bill" className="space-y-2 rounded-card bg-surface-alt p-3">
    <h3 className="text-body-sm font-semibold text-ink">The tenant's monthly bill</h3>
    <ul className="space-y-1 text-body-sm">
      {rows.map((r) => <li key={r.label} className="flex justify-between gap-3">
        <span className="text-ink">{r.label}<span className="text-ink-muted"> · {r.fund === 'building_fund' ? 'building fund' : 'owner'}</span></span>
        <span className="tabular text-ink">{formatCurrency(r.amount)}</span>
      </li>)}
      <li className="flex justify-between gap-3 border-t border-hairline pt-1 font-semibold">
        <span>Each month</span><span className="tabular">{formatCurrency(tenantTotal)}</span>
      </li>
    </ul>
    <p className="text-body-sm text-ink-muted">
      {ownerTotal > 0 && ownersApply
        ? hasOwner
          ? `With no tenant, the owner pays ${formatCurrency(ownerTotal)} a month (${vacant.map((r) => r.label.toLowerCase()).join(', ')}).`
          : `With no tenant, nobody is billed: choose the unit's owner so they pay ${formatCurrency(ownerTotal)} a month.`
        : 'With no tenant, nothing is billed.'}
    </p>
  </section>
}
